import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Copy,
  Check,
  Eye,
  EyeOff,
  Loader2,
  KeyRound,
  Webhook,
  RefreshCw,
  Zap,
  Radio,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { API_BASE_URL } from '@/lib/apiBase'
import {
  webhooksApi,
  TRIGGER_SCENARIOS,
  type TriggerResultType,
  type WebhookDelivery,
  type WebhookOutcome,
  type WebhookType,
  type WebhookSubscription,
  type TriggerResult,
  TRIGGER_ADT_EVENTS,
} from '@/api/webhooksApi'
import { useLisSourceSchemas } from '@/hooks/useLisConfig'
import { cn } from '@/lib/utils'
import { fallbackReceiverUrl, receiverUrlView } from '@/lib/webhookReceiverUrl'

// Placeholder signing secret. Non-functional for now — signature verification
// isn't wired up on the receiver yet; this is only the UI surface for it.
const SIGNING_SECRET = 'whsec_3f9a1c7e42b84d06a5e1f8c2b90d47ae'
const SIGNATURE_HEADER = 'X-CGT-Signature'

const TYPE_LABEL: Record<WebhookType, string> = {
  instrument_results: 'Instrument results',
  cases: 'Cases',
  event_patient_new: 'Patient event',
  event_instrument_result: 'Instrument event',
  event_patient_encounter: 'Epic visit',
  event_patient_demographics: 'Patient details',
  event_lis_result: 'Laboratory result',
}

/**
 * The events a fresh `events` subscription registers for, mirroring
 * `CHANNEL_DEFAULTS` in the backend's webhook-subscriptions service. Shown only
 * until a real subscription exists to read the list from.
 */
const DEFAULT_CHANNEL_EVENTS = [
  'patient.new',
  'instrument.result',
  'patient.encounter.upsert',
  'patient.demographics.upsert',
  'lis.result',
]

/** Which emit the dialog fires. */
type TriggerMode =
  | 'patient.new'
  | 'patient.encounter.upsert'
  | 'patient.demographics.upsert'
  | 'instrument.result'
  | 'custom'


export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7 text-muted-foreground opacity-60 transition-opacity hover:opacity-100"
      title={label ?? 'Copy'}
      onClick={async () => {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  )
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{children}</p>
  )
}

function MonoField({ value }: { value: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2">
      <code className="min-w-0 flex-1 truncate font-mono text-xs">{value}</code>
      <CopyButton value={value} />
    </div>
  )
}

const OUTCOME_STYLE: Record<WebhookOutcome, string> = {
  processed: 'bg-green-500',
  // A success that changed nothing — green would claim it landed new data, and
  // amber would read as a problem. Deliberately quieter than both.
  no_change: 'bg-green-500/40',
  received: 'bg-muted-foreground/40',
  invalid: 'bg-amber-500',
  failed: 'bg-red-500',
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const s = Math.round(diff / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}

export function WebhooksPanel({ tenantId }: { tenantId: string | null }) {
  const queryClient = useQueryClient()
  const [revealSecret, setRevealSecret] = useState(false)
  const [payloadView, setPayloadView] = useState<WebhookDelivery | null>(null)

  // Subscriptions
  const [subBusy, setSubBusy] = useState(false)
  const [subError, setSubError] = useState<string | null>(null)

  // Trigger (mock-driven)
  const [triggerOpen, setTriggerOpen] = useState(false)
  const [triggerMode, setTriggerMode] = useState<TriggerMode>('patient.new')
  const [patientId, setPatientId] = useState('')
  const [treatmentCode, setTreatmentCode] = useState('')
  // Epic visit (patient.encounter.upsert). `encounterNumber` blank = let the
  // backend target the patient's open demo visit, so A03 closes something the
  // Patient Visit History tab is already showing.
  const [adtTrigger, setAdtTrigger] = useState('A01')
  const [encounterNumber, setEncounterNumber] = useState('')
  const [din, setDin] = useState('')
  // The mock now generates the result payload; pick which one with resultType +
  // an optional scenario (empty = the type's happy path).
  const [resultType, setResultType] = useState<TriggerResultType>('nc200')
  const [scenario, setScenario] = useState('')
  // Custom JSON: delivered to the receiver exactly as typed. No template — any
  // event a source might send can be tried, including a lis.result.
  const [rawJson, setRawJson] = useState('')
  // The source's registered example, offered as a starting point only.
  const exampleMessage = (useLisSourceSchemas().data ?? []).find((sch) => sch.active && sch.sample)
    ?.sample
  const [triggerBusy, setTriggerBusy] = useState(false)
  const [triggerResult, setTriggerResult] = useState<TriggerResult | null>(null)
  const [triggerError, setTriggerError] = useState<string | null>(null)

  const { data: deliveries = [], isLoading } = useQuery<WebhookDelivery[]>({
    queryKey: ['webhook-deliveries', tenantId],
    queryFn: () => webhooksApi.listDeliveries(tenantId as string),
    enabled: !!tenantId,
    refetchInterval: 5000,
  })

  const { data: subscriptions = [] } = useQuery<WebhookSubscription[]>({
    queryKey: ['webhook-subscriptions', tenantId],
    queryFn: () => webhooksApi.listSubscriptions(tenantId as string),
    enabled: !!tenantId,
  })

  const eventsSub = subscriptions.find((s) => s.channel === 'events') ?? null
  // What this channel actually carries. From the live subscription when there is
  // one, so the label cannot claim an event the hook was never registered for —
  // a subscription made before `patient.encounter.upsert` existed still lists
  // only two, and that is the thing worth seeing.
  const channelEvents = eventsSub?.events?.length ? eventsSub.events : DEFAULT_CHANNEL_EVENTS

  function refreshDeliveries() {
    queryClient.invalidateQueries({ queryKey: ['webhook-deliveries', tenantId] })
  }
  function refreshSubscriptions() {
    queryClient.invalidateQueries({ queryKey: ['webhook-subscriptions', tenantId] })
  }

  async function runSub(fn: () => Promise<unknown>) {
    if (!tenantId) return
    setSubBusy(true)
    setSubError(null)
    try {
      await fn()
      refreshSubscriptions()
    } catch (e) {
      setSubError(e instanceof Error ? e.message : 'Subscription action failed.')
    } finally {
      setSubBusy(false)
    }
  }

  async function sendTrigger() {
    if (!tenantId) return
    setTriggerBusy(true)
    setTriggerError(null)
    setTriggerResult(null)
    try {
      let res: TriggerResult
      if (triggerMode === 'patient.new') {
        res = await webhooksApi.triggerPatientNew(tenantId, {
          patientId,
          treatment: { code: treatmentCode },
        })
      } else if (triggerMode === 'patient.demographics.upsert') {
        res = await webhooksApi.triggerPatientDemographics(tenantId, { patientId })
      } else if (triggerMode === 'patient.encounter.upsert') {
        res = await webhooksApi.triggerPatientEncounter(tenantId, {
          patientId,
          trigger: adtTrigger,
          encounterNumber: encounterNumber || undefined,
        })
      } else if (triggerMode === 'custom') {
        let body: unknown
        try {
          body = JSON.parse(rawJson)
        } catch (err) {
          throw new Error(`Not valid JSON: ${(err as Error).message}`)
        }
        // Whatever the receiver answers is the result, shown as-is below.
        res = { response: await webhooksApi.triggerRawEvent(tenantId, body) }
      } else {
        res = await webhooksApi.triggerInstrumentResult(tenantId, {
          din,
          resultType,
          scenario: scenario || undefined,
        })
      }
      setTriggerResult(res)
      // The backend echoes the visit it drove. Pinning it here is what makes the
      // A01 → A03 sequence work from the dialog: the next trigger targets the
      // same visit instead of deriving a fresh default.
      const usedEncounter = (res as { encounterNumber?: string }).encounterNumber
      if (usedEncounter) setEncounterNumber(usedEncounter)
      refreshDeliveries()
    } catch (e) {
      setTriggerError(e instanceof Error ? e.message : 'Trigger failed.')
    } finally {
      setTriggerBusy(false)
    }
  }

  if (!tenantId) {
    return (
      <div className="rounded-xl border border-dashed py-16 text-center text-sm text-muted-foreground">
        Select a tenant to view its webhook endpoints.
      </div>
    )
  }

  const receiver = receiverUrlView({
    registered: eventsSub?.registeredUrl,
    expected: eventsSub?.expectedUrl,
    fallback: fallbackReceiverUrl(API_BASE_URL, window.location.origin, tenantId),
  })

  return (
    <div className="space-y-5">
      {/* Panel header — identity + panel-level actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Webhook className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold leading-tight">Webhooks</h3>
            <p className="text-xs text-muted-foreground">Push deliveries from the integration-service.</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" onClick={() => setTriggerOpen(true)}>
            <Zap className="mr-2 h-3.5 w-3.5" />
            Trigger event
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground opacity-60 hover:opacity-100"
            title="Refresh deliveries"
            onClick={refreshDeliveries}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Subscription + deliveries, side by side on desktop */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
      {/* Events channel — the subscription plus the receiver URLs it delivers to */}
      <Card className="border-border/60 shadow-none">
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <Radio className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-semibold">Subscription</h4>
          </div>

          {subError && <p className="text-xs text-destructive">{subError}</p>}

          <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
            <span
              className={cn(
                'h-2 w-2 shrink-0 rounded-full',
                eventsSub?.status === 'active' ? 'bg-green-500' : eventsSub ? 'bg-amber-500' : 'bg-muted-foreground/40',
              )}
              title={eventsSub?.status ?? 'not subscribed'}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">events channel</p>
              <p className="truncate text-xs text-muted-foreground" title={channelEvents.join(', ')}>
                {channelEvents.join(', ')}
              </p>
            </div>
            {!eventsSub ? (
              <Button size="sm" disabled={subBusy} onClick={() => runSub(() => webhooksApi.createSubscription(tenantId, { channel: 'events' }))}>
                {subBusy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                Subscribe
              </Button>
            ) : (
              <div className="flex items-center gap-1.5">
                {(eventsSub.status === 'missing' || receiver.mismatch) && (
                  <Button size="sm" variant="outline" disabled={subBusy} onClick={() => runSub(() => webhooksApi.resubscribe(tenantId, eventsSub.id))}>
                    Re-subscribe
                  </Button>
                )}
                <Button size="sm" variant="ghost" disabled={subBusy} onClick={() => runSub(() => webhooksApi.removeSubscription(tenantId, eventsSub.id))}>
                  Unsubscribe
                </Button>
              </div>
            )}
          </div>

          {/* Receiver URL the integration-service pushes events to */}
          <div className="space-y-2 border-t border-border/60 pt-3">
            <p className="text-xs text-muted-foreground">
              The integration-service pushes{' '}
              {channelEvents.map((e, i) => (
                <span key={e}>
                  {i > 0 && (i === channelEvents.length - 1 ? ' and ' : ', ')}
                  <span className="font-medium text-foreground">{e}</span>
                </span>
              ))}{' '}
              events to this receiver URL:
            </p>
            <MonoField value={receiver.shown} />
            {receiver.mismatch && (
              <p className="text-xs text-amber-600">
                Events are going to a different address than this app expects. Re-subscribe to fix it.
              </p>
            )}

            {/* Signing secret — hidden for now (signature verification not wired up yet). */}
            {false && (
              <div className="space-y-1.5 border-t border-border/60 pt-4">
                <div className="flex items-center gap-1.5">
                  <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                  <Eyebrow>Signing secret</Eyebrow>
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2">
                  <code className="min-w-0 flex-1 truncate font-mono text-xs">
                    {revealSecret ? SIGNING_SECRET : `whsec_${'•'.repeat(24)}`}
                  </code>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRevealSecret((v) => !v)}>
                    {revealSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                  <CopyButton value={SIGNING_SECRET} label="Copy secret" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Sent as the <code className="font-mono text-[11px]">{SIGNATURE_HEADER}</code> header on each delivery.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent deliveries — the primary content */}
      <Card className="border-border/60 shadow-none">
        <CardContent className="p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold">Recent deliveries</h4>
              {deliveries.length > 0 && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {deliveries.length}
                </span>
              )}
            </div>
            <span className="text-[11px] text-muted-foreground">Updates every 5s</span>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : deliveries.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No deliveries yet. Use <span className="font-medium text-foreground">Trigger event</span> to push one.
            </p>
          ) : (
            <div className="max-h-96 divide-y divide-border/60 overflow-y-auto">
              {deliveries.map((d) => (
                <div key={d.id} className="flex items-center gap-3 py-2.5 text-sm">
                  <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', OUTCOME_STYLE[d.outcome])} title={d.outcome} />
                  <span className="w-32 shrink-0 truncate text-muted-foreground">{TYPE_LABEL[d.type] ?? d.type}</span>
                  <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">
                    {d.din ?? '—'}
                    {d.productCode ? ` · ${d.productCode}` : ''}
                  </span>
                  {d.resultCount != null && <span className="shrink-0 text-xs text-muted-foreground">{d.resultCount} rows</span>}
                  <span className="w-16 shrink-0 text-right text-xs text-muted-foreground">{relativeTime(d.receivedAt)}</span>
                  <Button variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground" onClick={() => setPayloadView(d)}>
                    View payload
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Trigger event (mock-driven) */}
      <Dialog open={triggerOpen} onOpenChange={setTriggerOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Trigger an event</DialogTitle>
            <DialogDescription>
              Fires the integration-service emit so it pushes a real event back to this tenant's receiver.
            </DialogDescription>
          </DialogHeader>

          <div className="-mx-1 max-h-[65vh] space-y-4 overflow-y-auto px-1 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="trig-event">Event type</Label>
              <Select
                value={triggerMode}
                onValueChange={(v) => {
                  setTriggerMode(v as TriggerMode)
                  setTriggerResult(null)
                  setTriggerError(null)
                }}
              >
                <SelectTrigger id="trig-event">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="patient.new">patient.new — open a procedure</SelectItem>
                  <SelectItem value="patient.encounter.upsert">
                    patient.encounter.upsert — record an Epic visit
                  </SelectItem>
                  <SelectItem value="patient.demographics.upsert">
                    patient.demographics.upsert — update patient details
                  </SelectItem>
                  <SelectItem value="instrument.result">instrument.result — return a result</SelectItem>
                  <SelectItem value="custom">Custom JSON — send any payload (e.g. lis.result)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {triggerMode === 'custom' ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="trig-raw">Payload (JSON)</Label>
                  {exampleMessage ? (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline hover:text-foreground"
                      onClick={() => setRawJson(JSON.stringify(exampleMessage, null, 2))}
                    >
                      Use the source&rsquo;s example message
                    </button>
                  ) : null}
                </div>
                <Textarea
                  id="trig-raw"
                  value={rawJson}
                  onChange={(e) => setRawJson(e.target.value)}
                  rows={12}
                  spellCheck={false}
                  placeholder='{ "event": "lis.result", "orderId": "T21-…", … }'
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Sent to this tenant&rsquo;s receiver exactly as written — the same path a real delivery
                  takes, recorded as sent by you. The <span className="font-mono">event</span> field says
                  what kind of delivery it is.
                </p>
              </div>
            ) : triggerMode === 'instrument.result' ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="trig-din">DIN</Label>
                  <Input id="trig-din" value={din} onChange={(e) => setDin(e.target.value)} placeholder="paste a DIN" className="font-mono text-xs" />
                  <p className="text-xs text-muted-foreground">
                    Mint a DIN from a procedure with <span className="font-medium text-foreground">Order Test Results</span>, then paste it here.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="trig-result-type">Result type</Label>
                    <Select
                      value={resultType}
                      onValueChange={(v) => {
                        setResultType(v as TriggerResultType)
                        setScenario('') // reset scenario when the instrument changes
                      }}
                    >
                      <SelectTrigger id="trig-result-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(TRIGGER_SCENARIOS) as TriggerResultType[]).map((rt) => (
                          <SelectItem key={rt} value={rt}>
                            {TRIGGER_SCENARIOS[rt].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="trig-scenario">Scenario</Label>
                    <Select
                      value={scenario || 'default'}
                      onValueChange={(v) => setScenario(v === 'default' ? '' : v)}
                    >
                      <SelectTrigger id="trig-scenario">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Happy path (default)</SelectItem>
                        {TRIGGER_SCENARIOS[resultType].scenarios.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  The integration-service generates the result payload from your selection. No sample
                  data needed.
                </p>
              </div>
            ) : triggerMode === 'patient.encounter.upsert' ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="trig-enc-pid">Patient MRN</Label>
                  <Input
                    id="trig-enc-pid"
                    value={patientId}
                    onChange={(e) => setPatientId(e.target.value)}
                    placeholder="e.g. MRN-2026-777"
                  />
                  <p className="text-xs text-muted-foreground">
                    The MRN need not exist yet — an admission for an unknown patient creates one,
                    exactly as a real Epic feed would.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="trig-adt">ADT trigger</Label>
                    <Select value={adtTrigger} onValueChange={setAdtTrigger}>
                      <SelectTrigger id="trig-adt">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TRIGGER_ADT_EVENTS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="trig-enc-no">Visit number</Label>
                    <Input
                      id="trig-enc-no"
                      value={encounterNumber}
                      onChange={(e) => setEncounterNumber(e.target.value)}
                      placeholder="auto"
                      className="font-mono text-xs"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave the visit number on <span className="font-medium text-foreground">auto</span>{' '}
                  to target this patient's open demo visit, then fire{' '}
                  <span className="font-medium text-foreground">A03</span> to watch it close on the
                  Patient Visit History tab. The number is pinned here after the first send, so a
                  follow-up trigger drives the same visit.
                </p>
              </div>
            ) : triggerMode === 'patient.demographics.upsert' ? (
              <div className="space-y-1.5">
                <Label htmlFor="trig-demo-pid">Patient MRN</Label>
                <Input
                  id="trig-demo-pid"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  placeholder="e.g. MRN-2026-777"
                />
                <p className="text-xs text-muted-foreground">
                  Sends updated details for this patient, as an Epic A08 would. No procedure is opened.
                  An unknown MRN creates the patient.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="trig-pid">Patient MRN</Label>
                  <Input id="trig-pid" value={patientId} onChange={(e) => setPatientId(e.target.value)} placeholder="e.g. MRN-2026-777" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="trig-code">Treatment code (matches a procedure's product code)</Label>
                  <Input id="trig-code" value={treatmentCode} onChange={(e) => setTreatmentCode(e.target.value)} placeholder="e.g. AUTO-PBSC" />
                </div>
              </div>
            )}

            {triggerError && <p className="text-xs text-destructive">{triggerError}</p>}

            {triggerResult && (
              <div className="space-y-1.5">
                <Eyebrow>Delivery report</Eyebrow>
                {triggerMode === 'custom' ? (
                  <pre className="max-h-48 overflow-auto rounded-lg border border-border/60 bg-muted/40 p-2 font-mono text-[11px]">
                    {JSON.stringify((triggerResult as { response?: unknown }).response, null, 2)}
                  </pre>
                ) : (triggerResult.deliveries ?? []).length === 0 ? (
                  <p className="text-xs text-amber-600">
                    No subscribers matched — is the events channel subscribed?
                  </p>
                ) : (
                  (triggerResult.deliveries ?? []).map((del, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className={cn('h-1.5 w-1.5 rounded-full', del.delivered ? 'bg-green-500' : 'bg-destructive')} />
                      <span className={del.delivered ? 'text-green-600' : 'text-destructive'}>
                        {del.delivered ? 'delivered' : 'failed'} · HTTP {del.statusCode ?? '—'}
                        {del.error ? ` · ${del.error}` : ''}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTriggerOpen(false)}>
              Close
            </Button>
            <Button onClick={sendTrigger} disabled={
                triggerBusy ||
                (triggerMode === 'custom' && !rawJson.trim()) ||
                (triggerMode === 'instrument.result' && !din) ||
                (triggerMode !== 'instrument.result' && triggerMode !== 'custom' && !patientId)
              }>
              {triggerBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
              Fire event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payload viewer */}
      <Dialog open={!!payloadView} onOpenChange={(o) => !o && setPayloadView(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Delivery payload</DialogTitle>
            <DialogDescription>
              {payloadView ? TYPE_LABEL[payloadView.type] ?? payloadView.type : ''}
              {payloadView ? ` · ${relativeTime(payloadView.receivedAt)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded-lg border border-border/60 bg-muted/40 p-3 font-mono text-xs">
            {JSON.stringify(payloadView?.payload ?? {}, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  )
}
